import type { BoxProps } from "@qws-org/ui-kit";
import { Box, Button, Flex, Image, Markdown, Title } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, Gallery_Slider, Pages } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { PaginationDots } from "~/features/gallery-slider/components/PaginationDots";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type GallerySliderProps = {
  data: Gallery_Slider;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (control: Action) => boolean;
} & BoxProps;
const getInactiveIndexes = (
  activeIndex: number,
  length: number,
): [number, number] => {
  if (length <= 1) {
    return [-1, -1];
  }

  const start = activeIndex === 0 ? length - 1 : activeIndex - 1;
  const end = activeIndex === length - 1 ? 0 : activeIndex + 1;

  return [start, end];
};
export const GallerySlider: FC<GallerySliderProps> = (props) => {
  const { data, getUrl, onCallAction, isControlInactive, ...rest } = props;
  const { getDirectusImage } = useDirectus();
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const inactivesIndexes = getInactiveIndexes(
    activeIndex,
    data?.images?.length ?? 0,
  );

  const controlAction = data?.action;
  const isInactive = controlAction ? isControlInactive(controlAction) : true;
  const action = data.action?.action?.[0];
  let href;
  if (isExternalLink(action?.item)) {
    href = action.item.href ?? undefined;
  }
  if (isInternalLink(action?.item)) {
    href = action.item.page ? getUrl(action.item.page) : undefined;
  }
  const handlePressButton = (): void => {
    if (controlAction) {
      onCallAction(controlAction);
    }
  };

  const breakpointsSettings =
    data.variant === "phone"
      ? {
          1264: {
            coverflowEffect: {
              rotate: 0,
              stretch: 0,
              scale: 0.82,
            },
            spaceBetween: 80,
            slidesPerView: 3,
          },

          1024: {
            coverflowEffect: {
              rotate: 0,
              stretch: 0,
              scale: 0.82,
            },
            spaceBetween: 40,
            slidesPerView: 3,
          },

          768: {
            coverflowEffect: {
              rotate: 0,
              stretch: 0,
              scale: 0.82,
            },
            spaceBetween: 30,
            slidesPerView: 3,
          },

          340: {
            slidesPerView: 1,
          },
        }
      : {
          1264: {
            coverflowEffect: {
              stretch: 602,
              rotate: 0,
              scale: 0.82,
            },
            slidesPerView: 1.5,
          },

          1024: {
            coverflowEffect: { stretch: 520, rotate: 0, scale: 0.82 },
            slidesPerView: 1.5,
          },

          768: {
            coverflowEffect: { stretch: 400, rotate: 0, scale: 0.82 },
            slidesPerView: 1.5,
          },

          340: {
            slidesPerView: 1,
          },
        };

  return (
    <Box p={{ py: 32, px: 24 }} {...rest} d={{ p: { py: 80 }, ...rest.d }}>
      <Title
        as="h2"
        horizontalAlign="center"
        fontWeight="mobile.bold"
        size="medium"
        md={{
          fontWeight: "desktop.semibold",
          fontSize: "title.desktop.medium",
          lineHeight: "title.desktop.medium",
          m: { mb: 24 },
        }}
        m={{ mb: 16 }}
        d={{ m: { mb: 64 } }}
      >
        {data.title}
      </Title>

      {data.description && (
        <Markdown alignment="center" m={{ mb: 24 }}>
          {data.description}
        </Markdown>
      )}
      <Box m={{ mb: 24 }} d={{ m: { mb: 40 } }}>
        <Swiper
          className="w-full"
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          speed={500}
          effect="coverflow"
          autoplay={
            data.autoplay_delay === 0
              ? false
              : {
                  delay: data.autoplay_delay ?? 5000,
                  pauseOnMouseEnter: true,
                  disableOnInteraction: false,
                }
          }
          breakpoints={breakpointsSettings}
          centeredSlides={true}
          loop={true}
          grabCursor={true}
          modules={[EffectCoverflow, Autoplay]}
        >
          {data.pictures
            ? data.pictures?.map((picture, index) => {
                const pic = picture.picture_id;

                const mobileImage = pic?.image;
                const mobileColor = pic?.color ?? undefined;
                const mobileWidth = pic?.mobile_image_width ?? undefined;
                const mobileHeight = pic?.mobile_image_height ?? undefined;

                const tabletImage = pic?.tablet_image ?? mobileImage;
                const tabletColor = pic?.tablet_color ?? mobileColor;
                const tabletWidth = pic?.tablet_image_width ?? mobileWidth;
                const tabletHeight = pic?.tablet_image_height ?? mobileHeight;

                const desktopImage = pic?.desktop_image ?? tabletImage;
                const desktopColor = pic?.desktop_color ?? tabletColor;
                const desktopWidth = pic?.desktop_image_width ?? tabletWidth;
                const desktopHeight = pic?.desktop_image_height ?? tabletHeight;

                const isActive = index === activeIndex;

                return (
                  <SwiperSlide
                    key={picture?.id}
                    className="!flex !justify-center !items-center"
                  >
                    <Image
                      value={
                        mobileImage?.id
                          ? getDirectusImage(mobileImage.id, {
                              height: mobileHeight,
                              width: mobileWidth,
                              format: "webp",
                            })
                          : (mobileColor ?? "")
                      }
                      width={mobileWidth}
                      height={mobileHeight}
                      border={{
                        radius: "8px",
                      }}
                      bg={
                        data.variant === "phone" && isActive
                          ? "/gallery-slider-frame.png"
                          : ""
                      }
                      p={
                        data.variant === "phone" && isActive
                          ? { px: 20, py: 28 }
                          : 0
                      }
                      mdValue={
                        tabletImage?.id
                          ? getDirectusImage(tabletImage.id, {
                              height: tabletHeight,
                              width: tabletWidth,
                              format: "webp",
                            })
                          : (tabletColor ?? "")
                      }
                      md={{
                        border:
                          data.variant === "banner" && isActive
                            ? {
                                width: "3.2px",
                                color: "border-accent",
                                radius: "8px",
                              }
                            : {
                                width: "0",
                                radius: "16px",
                              },
                        width: tabletWidth,
                        height: tabletHeight,
                      }}
                      dValue={
                        desktopImage?.id
                          ? getDirectusImage(desktopImage.id, {
                              height: desktopHeight,
                              width: desktopWidth,
                              format: "webp",
                            })
                          : (desktopColor ?? "")
                      }
                      d={{
                        width: desktopWidth,
                        height: desktopHeight,
                      }}
                      overflow="hidden"
                      opacity={!isActive ? 0.7 : 1}
                    />
                  </SwiperSlide>
                );
              })
            : data.images?.map((image, index) => {
                const isActive = index === activeIndex;

                return (
                  <SwiperSlide
                    key={image?.id}
                    className="!flex !justify-center !items-center"
                  >
                    <Image
                      border={{
                        radius: "8px",
                      }}
                      md={{
                        border: {
                          width: isActive ? "3.2px" : "0",
                          color: "border-accent",
                          radius: isActive ? "8px" : "16px",
                        },
                      }}
                      value={getDirectusImage(
                        image?.gallery_slider_images_id?.image?.id ?? "",
                        { height: 200, format: "webp" },
                      )}
                      mdValue={getDirectusImage(
                        image?.gallery_slider_images_id?.image?.id ?? "",
                        { height: 368, format: "webp" },
                      )}
                      className={`${inactivesIndexes.includes(index) || isActive ? "!object-cover" : "opacity-0"} overflow-hidden`}
                    />
                  </SwiperSlide>
                );
              })}
        </Swiper>
      </Box>
      {data.variant === "banner" && (
        <PaginationDots {...props} activeIndex={activeIndex} />
      )}

      {!isInactive && (
        <Flex m={{ mx: "auto" }} flexDirection="column" align="center">
          <Button
            onPress={handlePressButton}
            href={href}
            size="small"
            md={{
              fontWeight: "desktop.bold",
              lineHeight: "button.desktop.medium",
              m: { mt: 40 },
              p: { px: 24, py: 12 },
            }}
            m={{ mt: 40 }}
            p={{ px: 24, py: 12 }}
          >
            {data.action_text}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
